import type { File } from "@/shared/state/visualization/schema/v1/file";
import type {
  JointValue,
  ModelMesh,
} from "@/shared/state/visualization/schema/v1/three-d";

/**
 * What to do about a model the user has just chosen for an entity.
 *
 * `keep` means the entity already draws exactly this, so nothing is dispatched
 * and nothing is fetched. `import` carries the joint values the import should
 * treat as already decided — see {@link planModelRepick} for when there are any.
 */
export type ModelRepick =
  | { action: "keep" }
  | { action: "import"; jointValues?: Readonly<Record<string, JointValue>> };

/** Whether two file references name the same file. */
function sameFile(left: File | undefined, right: File | undefined): boolean {
  return left?.fileId === right?.fileId;
}

/**
 * Whether a re-pick is a change at all, and what of the entity's joint bindings
 * survives it.
 *
 * ## Why the source is what identifies a robot
 *
 * A robot description ingested into the platform produces a glTF artifact, and
 * re-ingesting it produces a *new* one. The artifact is therefore a cache key,
 * not an identity: the same robot can resolve to a different `file` from one
 * week to the next. `source` is the file the user chose and the thing that
 * persists, so it is what "the same model" is decided by.
 *
 * ## What happens to `mesh.jointValues`
 *
 * They are keyed by joint name, and a name only means something within one
 * robot. So:
 *
 * - **Same source, same artifact** — nothing to do. Returned as `keep` rather
 *   than as an import that happens to produce the same thing, so re-picking what
 *   is already there costs no fetch and leaves the mesh spec untouched.
 * - **Same source, different artifact** — the robot was converted again. The
 *   joint names are the same robot's, so every binding is carried over; a
 *   refresh that silently unbound a 30-joint arm would be worse than no refresh.
 * - **Anything else** — a different robot, and nothing is carried. Matching
 *   joint names across two robots is not evidence they mean the same thing, and
 *   a carried value is *stickier* than a dropped one: `withDefaultBindings`
 *   reads a present key as "the user decided this" and will not apply the new
 *   model's own declared default over it. So a wrongly-kept binding both drives
 *   the wrong joint and suppresses the right default, with nothing said. Cleared
 *   instead, every joint of the new model is "never decided", which is exactly
 *   the state a first import starts from.
 *
 * A joint the new model does not have is dropped either way — `joints` and
 * `jointValues` are replaced wholesale by the import, and `withDefaultBindings`
 * keeps only the names the new joint list carries.
 *
 * @param current the model the entity draws today, if it draws one
 * @param picked the artifact to load and the description it was generated from
 */
export function planModelRepick(
  current: ModelMesh | undefined,
  picked: { model: File; source: File },
): ModelRepick {
  if (current === undefined) {
    return { action: "import" };
  }
  if (!sameFile(current.source, picked.source)) {
    return { action: "import" };
  }
  if (sameFile(current.file, picked.model)) {
    return { action: "keep" };
  }
  return {
    action: "import",
    ...(current.jointValues ? { jointValues: current.jointValues } : {}),
  };
}
